import { apiClient } from '@/shared/api/apiClient'
import { OperatorOrganization } from '@/features/auth/api/AuthModel'
import { EmissionQualifyingParams } from '@/shared/api/EmissionQualifyingParamsModel'
import { Installation } from '@/features/installations/api/InstallationModel'
import { ProductionRoute } from '@/features/production-routes/api/ProductionRouteModel'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { MethodAggregatedEmissions } from './finalizedReports'
import { MaterialInputState } from '@/features/mes-integration/components/MesReportEdit'

type ChalcoActivityQueryParams = {
  smallRollNumbers: string[]
}

export type ChalcoActivityResponse = {
  startDate: string
  endDate: string
  smallRollsWithTracability: string[]
  activityLevel: number
  assetsSummary: {
    assetName: string
    assetNameEnglish: string
    fuelConsumptionM3: number
    electricityConsumptionMwh: number
  }[]
  totalElectricityConsumptionMwh: number
  precursors: {
    name: string
    category: string
    weightTonnes: number
    shareOfMaterialUsed: number
  }[]
}

export type MesReportPayload = {
  batchNumbers: string[]
  isFullCalendarYear: boolean
  specificEmbeddedDirect: number
  specificEmbeddedIndirect: number
  electricityEmissionFactor: string | null
  productionMethodCode: string
  specificElectricityConsumption: number
  emissionQualifyingParams: EmissionQualifyingParams
  locale: string
  methodAggregatedEmissions: MethodAggregatedEmissions[]
  chalcoActivity: ChalcoActivityResponse | null
  activityLevel: number | null
  fuelType: string | null
  density: string | null
  materialInput: MaterialInputState | null
  netCalorificValue: string | null
  fuelEmissionFactor: string | null
  version: number
}

export const CHALCO_DATA_VERSION = 1

const defaultMesReportPayload: MesReportPayload = {
  batchNumbers: [],
  chalcoActivity: null,
  materialInput: null,
  fuelType: null,
  density: null,
  electricityEmissionFactor: null,
  netCalorificValue: null,
  fuelEmissionFactor: null,
  activityLevel: 0,
  isFullCalendarYear: false,
  specificEmbeddedDirect: 0,
  specificEmbeddedIndirect: 0,
  productionMethodCode: '',
  specificElectricityConsumption: 0,
  emissionQualifyingParams: {},
  locale: '',
  methodAggregatedEmissions: [],
  version: CHALCO_DATA_VERSION
}

const CHALCO_REPORTS_QUERY_KEY = 'chalco-reports'
const CHALCO_ACTIVITY_QUERY_KEY = 'chalco-activity'

type ChalcoReport = {
  id: string
  installationId: string
  productionRouteId: string
  startDate: string
  endDate: string
  createdAt: string
  data: MesReportPayload
  isSubmitted: boolean
  submittedAt: string
}

type ChalcoMesReportListItem = ChalcoReport & {
  installation: {
    id: string
    name: string
  }
  productionRoute: {
    id: string
    name: string
  }
}

type ChalcoReportWithResources = ChalcoReport & {
  installation: Installation & {
    organization: OperatorOrganization
  }
  productionRoute: ProductionRoute
}

export const useGetChalcoReports = () => {
  return useQuery<ChalcoMesReportListItem[], Error>({
    queryKey: [CHALCO_REPORTS_QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<ChalcoMesReportListItem[]>(
        '/api/v1/integrations/chalco/reports'
      )

      return res.data
    }
  })
}

export const useCreateChalcoReport = () => {
  const queryClient = useQueryClient()

  return useMutation<ChalcoReportWithResources, Error, any>({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post<ChalcoReportWithResources>(
        '/api/v1/integrations/chalco/reports',
        {
          ...payload,
          data: defaultMesReportPayload
        }
      )

      return res.data
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: [CHALCO_REPORTS_QUERY_KEY] })
    }
  })
}

export const useUpdateChalcoReport = (reportId: string) => {
  const queryClient = useQueryClient()

  return useMutation<ChalcoReportWithResources, Error, any>({
    mutationFn: async data => {
      const res = await apiClient.put<any>(`/api/v1/integrations/chalco/reports/${reportId}`, data)

      queryClient.setQueryData([CHALCO_REPORTS_QUERY_KEY, reportId], res.data)

      return res.data
    }
  })
}

export const useSubmitChalcoReport = (reportId: string) => {
  const queryClient = useQueryClient()

  return useMutation<ChalcoReport, Error, any>({
    mutationFn: async data => {
      const res = await apiClient.post<ChalcoReportWithResources>(
        `/api/v1/integrations/chalco/reports/${reportId}/submit`,
        data
      )
      queryClient.setQueryData([CHALCO_REPORTS_QUERY_KEY, reportId], res.data)
      return res.data
    }
  })
}

export const useGetChalcoReport = (reportId: string) => {
  const queryClient = useQueryClient()

  return useQuery<ChalcoReportWithResources, Error>({
    queryKey: [CHALCO_REPORTS_QUERY_KEY, reportId],
    queryFn: async () => {
      const res = await apiClient.get<ChalcoReportWithResources>(
        `/api/v1/integrations/chalco/reports/${reportId}`
      )

      if (res.data.data.chalcoActivity) {
        const key = [
          CHALCO_ACTIVITY_QUERY_KEY,
          res.data.data.chalcoActivity.smallRollsWithTracability.join(',')
        ]

        queryClient.setQueryData(key, res.data.data.chalcoActivity)
      }

      return res.data
    }
  })
}

export const useDeleteChalcoReport = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async reportId => {
      await apiClient.delete(`/api/v1/integrations/chalco/reports/${reportId}`)
      queryClient.invalidateQueries({ queryKey: [CHALCO_REPORTS_QUERY_KEY] })
    }
  })
}

export const useDownloadChalcoReportPdf = () => {
  const mutation = useMutation({
    mutationFn: async (payload: { reportId: string; filename: string }) => {
      const response = await apiClient.post<Blob>(
        `/api/v1/integrations/chalco/reports/${payload.reportId}/pdf`,
        {},
        {
          responseType: 'blob',
          timeout: 15000 // 15 seconds
        }
      )

      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = payload.filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    }
  })

  return mutation
}

export const useGetChalcoHealth = () => {
  return useQuery<{ isConnected: boolean }>({
    queryKey: ['chalco-health'],
    queryFn: async () => {
      const res = await apiClient.get<{ isConnected: boolean }>(
        '/api/v1/integrations/chalco/health'
      )

      return res.data
    },
    retry: false
  })
}

const errorSchema = z.object({
  message: z.string(),
  statusCode: z.number(),
  error: z.string()
})

export const useGetChalcoActivity = () => {
  return useMutation<ChalcoActivityResponse, Error, ChalcoActivityQueryParams>({
    mutationFn: async queryParams => {
      try {
        const res = await apiClient.get<ChalcoActivityResponse>('/api/v1/integrations/chalco', {
          params: queryParams,
          timeout: 180000,
          paramsSerializer: {
            indexes: null
          }
        })

        return res.data
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.code === 'ECONNABORTED') {
            throw new Error('TIMEOUT')
          }
          const parsedError = errorSchema.safeParse(err.response?.data)
          if (parsedError.success) {
            if (
              parsedError.data.statusCode === 404 &&
              parsedError.data.message === 'Chalco small rolls numbers not found'
            ) {
              throw new Error('SMALL_ROLLS_NOT_FOUND')
            }
          }
        }

        throw err
      }
    },
    retry: false
  })
}

type AvailableMonthsResponse = {
  month: number
  year: number
  hasFuelData: boolean
  hasElectricityData: boolean
}[]

const CHALCO_AVAILABLE_MONTHS_QUERY_KEY = 'chalco-available-months'

export const useGetChalcoIntegrationAvailableMonths = (enabled: boolean) => {
  return useQuery<AvailableMonthsResponse, Error>({
    queryKey: [CHALCO_AVAILABLE_MONTHS_QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<AvailableMonthsResponse>(
        '/api/v1/integrations/chalco/available-months'
      )

      return res.data
    },
    enabled
  })
}
